// Shared by the browser and static SEO build. Keep content in one place.
const GUIDES = [
  {
    slug: "how-to-calculate-excavation-volume",
    name: "How to Calculate Excavation Volume",
    title: "How to Calculate Excavation Volume | Bank, Loose & Truck Loads | Fieldlink",
    description: "How to calculate excavation volume for civil work — bank vs loose measure, bulking factors, truck loads and a worked example. With a free calculator.",
    calculator: { to: "/tools/excavation-volume-calculator", label: "Excavation Volume Calculator" },
    product: { to: "/earthworks-estimating-software", label: "See Earthworks Estimating", text: "Pricing the whole earthworks package? Fieldlink turns quantities into estimates with your own plant, labour and cartage rates." },
    quickAnswer: "Excavation volume for a rectangular dig-out is Length × Width × Average Depth, measured in bank (in-ground) cubic metres. To work out cartage, multiply by a bulking factor (commonly 20–30 %) to get loose volume, then divide by truck capacity for loads.",
    sections: [
      {
        h: "Bank volume vs loose volume",
        body: ["The single biggest mistake in excavation quantities is mixing up bank and loose measure. Bank volume is the material measured in the ground, before it's disturbed. The moment the digger breaks it out, the material bulks up — air gets between the particles and the same bank cubic metre now takes up more space on the truck."],
        list: ["Bank m³ — what you measure off the drawings and what the excavation is priced on", "Loose m³ — what you cart, tip and stockpile", "Compacted m³ — what fill becomes after compaction (usually less than bank)"],
      },
      {
        h: "The calculation",
        formula: ["Bank volume = Length × Width × Average depth", "Loose volume = bank volume × (1 + bulking %)", "Truck loads = loose volume ÷ truck capacity (round up)"],
        body: ["Use the average depth across the cut. If the depth varies a lot, split the excavation into sections and calculate each one — averaging one end at 0.5 m and the other at 3 m across a long cut will bite you."],
      },
      {
        h: "Worked example",
        example: ["Trench for a foundation: 42 m long × 3.2 m wide × 1.4 m average depth", "Bank volume = 42 × 3.2 × 1.4 = 188.16 m³", "Bulking at 25 % → loose volume = 188.16 × 1.25 = 235.2 m³", "9 m³ trucks → 235.2 ÷ 9 = 26.1 → 27 loads"],
      },
      {
        h: "Choosing a bulking factor",
        body: ["Bulking depends on the material. As a starting point: sands and gravels around 10–15 %, mixed fill around 20–25 %, clays commonly 25–30 %, and solid rock can exceed 50 % once blasted or ripped. If you cart the same ground regularly, your weighbridge history is the best factor you'll ever get."],
        list: ["Check payload too — wet clay usually hits the truck's legal weight before the bin is full", "Allow for batters and over-dig in your width and depth, they're not in the plan dimensions", "Topsoil strip is usually a separate item with its own depth"],
      },
    ],
    related: ["/guides/how-to-price-earthworks", "/tools/cut-fill-calculator", "/tools/truck-load-calculator", "/tools/truck-cycle-calculator"],
  },
  {
    slug: "how-to-calculate-pipe-fall",
    name: "How to Calculate Pipe Fall & Grade",
    title: "How to Calculate Pipe Fall and Grade | 1:X, %, mm/m | Fieldlink",
    description: "How to calculate pipe fall, grade percentage and 1:X gradient from invert levels — with reverse calculations and a worked drainage example. Free calculator included.",
    calculator: { to: "/tools/pipe-grade-calculator", label: "Pipe Grade Calculator" },
    product: { to: "/drainage-estimating-software", label: "See Drainage Estimating", text: "Pricing the whole drainage job? Fieldlink takes pipe runs from takeoff to estimate with your own laying rates." },
    quickAnswer: "Pipe fall is the difference between the upstream and downstream invert levels. Grade % = fall ÷ length × 100. The 1:X gradient is length ÷ fall — so 300 mm of fall over 30 m is 1 %, or 1:100.",
    sections: [
      {
        h: "The three ways drainlayers express grade",
        body: ["The same slope gets written three ways on drawings and consent conditions, and you need to move between them without thinking:"],
        list: ["Gradient 1:X — one unit of fall per X units of run (1:100 = 10 mm per metre)", "Grade % — fall as a percentage of length (1:100 = 1 %)", "mm per metre — literal fall per metre of pipe (1:100 = 10 mm/m)"],
      },
      {
        h: "The calculations",
        formula: ["Fall = upstream IL − downstream IL", "Grade % = fall ÷ length × 100", "Gradient 1:X where X = length ÷ fall", "Required fall for a target gradient = length ÷ X", "End invert = start invert − required fall"],
      },
      {
        h: "Worked example",
        example: ["Run: 42.5 m of 150 mm pipe", "Upstream IL 12.840, downstream IL 12.245", "Fall = 12.840 − 12.245 = 0.595 m (595 mm)", "Grade = 0.595 ÷ 42.5 × 100 = 1.4 %", "Gradient = 42.5 ÷ 0.595 = 1:71.4"],
      },
      {
        h: "Setting a pipe to a required gradient",
        body: ["Working the other way is just as common: you know the start invert and the minimum gradient, and you need the downstream invert. Required fall = length ÷ X. For 30 m at 1:100 that's 30 ÷ 100 = 0.3 m, so the end invert is 300 mm below the start.", "Minimum gradients come from the drainage standard and the network operator's requirements for the pipe size and use — check the approved drawings and local code of practice rather than assuming. Laying flatter than consented is a failed inspection and a re-lay."],
      },
    ],
    related: ["/guides/how-to-calculate-excavation-volume", "/tools/trench-volume-calculator", "/tools/pipe-grade-calculator"],
  },
  {
    slug: "how-to-price-earthworks",
    name: "How to Price Earthworks",
    title: "How to Price Earthworks in NZ | Quantities to Estimate | Fieldlink",
    description: "How to price an earthworks job — quantities, bulking, plant and truck productivity, disposal, overheads and margin, with a simple pricing framework and worked logic.",
    calculator: { to: "/tools/excavation-volume-calculator", label: "Excavation Volume Calculator" },
    product: { to: "/earthworks-estimating-software", label: "See Earthworks Estimating", text: "Fieldlink builds the full earthworks estimate — takeoff quantities priced with your plant, labour, cartage and disposal rates, then straight to quote and job budget." },
    quickAnswer: "Price earthworks from quantities up: measure cut, fill and strip in bank m³, convert to loose for cartage, work out plant and truck days from realistic production rates, add disposal and materials, then apply overheads and margin. Never price from a per-m³ 'going rate' you can't stand behind.",
    sections: [
      {
        h: "1. Get the quantities right first",
        body: ["Everything downstream is built on the quantities, so nail them before touching rates:"],
        list: ["Topsoil strip — area × strip depth, usually stockpiled or carted separately", "Bulk cut and fill — from the survey model or area × average depth as a check", "Net balance — surplus to cart off, or import required (with compaction allowance)", "Loose volumes — apply bulking to anything going on a truck"],
      },
      {
        h: "2. Plant and truck productivity",
        body: ["The cost driver in earthworks is time, not volume. A 14-tonne digger might load 60–100 loose m³ an hour depending on material, reach and truck availability — and it's the trucks that usually govern. Work out the truck cycle (load, haul, tip, return, queue) and check how many trucks the digger can actually keep busy. One truck short and the digger waits; one truck too many and you're paying a truck to queue."],
        formula: ["Digger days = loose volume ÷ (loading rate × productive hours/day)", "Truck loads = loose volume ÷ capacity (check payload too)", "Trucks required = cycle time ÷ load time (rounded sensibly)"],
      },
      {
        h: "3. Build the rate, don't guess it",
        body: ["Cost each element from your own numbers: machine charge-out (ownership + running + operator), truck rates (hourly or per load), disposal fees per tonne or per load, imported material supplied and placed, compaction plant, and supervision. Then add project overheads (establishment, traffic management, environmental controls) — these are real costs, not margin.", "Only after the true cost is built do you apply margin. Margin is on sell, not markup on cost — a 20 % markup is only a 16.7 % margin."],
      },
      {
        h: "4. The checks that save you",
        list: ["Sanity-check total truck loads — does the programme physically allow that many movements per day?", "Wet weather and undercut risk — price it, tag it, or exclude it explicitly", "Disposal site rules — clean fill vs managed fill vs contaminated changes the price completely", "Access and stand-down — small sites with slow cartage kill production rates"],
      },
    ],
    related: ["/guides/how-to-calculate-excavation-volume", "/guides/how-to-calculate-an-excavator-hourly-rate", "/tools/truck-cycle-calculator", "/tools/cut-fill-calculator", "/civil-rate-library-nz"],
  },
  {
    slug: "how-to-calculate-an-excavator-hourly-rate",
    name: "How to Calculate an Excavator Hourly Rate",
    title: "How to Calculate an Excavator Charge-Out Rate | Fieldlink",
    description: "How to build an excavator hourly charge-out rate — depreciation, finance, insurance, fuel, servicing, operator, overheads and margin, with a full worked example.",
    calculator: { to: "/tools/excavator-charge-out-rate-calculator-nz", label: "Excavator Charge-Out Calculator" },
    product: { to: "/civil-rate-library-nz", label: "See the Rate Library", text: "Save the rate you build into Fieldlink's Rate Library and every estimate prices your plant the same way." },
    quickAnswer: "An excavator charge-out rate is built from four blocks per hour: ownership (depreciation + finance + insurance ÷ annual hours), running costs (fuel + servicing + repairs), operator, and overhead — then divided by (1 − margin %) to get the sell rate. The most common mistake is dividing by hoped-for hours instead of realistic billable hours.",
    sections: [
      {
        h: "The four cost blocks",
        list: ["Ownership — depreciation (price − residual, spread over life), finance on the capital, insurance and compliance", "Running — fuel (L/hr × price), servicing, repairs and wear items (tracks, pins, buckets, teeth)", "Operator — fully-costed wage with on-costs, if quoting a wet rate", "Overhead — the machine's share of yard, admin, transporter, vehicles and management"],
      },
      {
        h: "The formulas",
        formula: ["Depreciation/yr = (purchase price − residual) ÷ ownership life", "Finance/yr = average capital × interest % (average capital ≈ (price + residual) ÷ 2)", "Ownership/hr = (depreciation + finance + insurance) ÷ annual hours", "Running/hr = fuel L/hr × fuel price + servicing + repairs", "Break-even/hr = (ownership + running + operator) × (1 + overhead %)", "Charge-out = break-even ÷ (1 − margin %)"],
      },
      {
        h: "Worked example — 14 t excavator",
        example: ["Purchase $180,000, residual $60,000 after 8 years, 1,400 hrs/yr", "Depreciation $15,000/yr + finance $8,400/yr + insurance $4,500/yr = $27,900 ÷ 1,400 = $19.93/hr ownership", "Fuel 14 L/hr × $1.55 + $8 servicing + $6 repairs = $35.70/hr running", "$55.63 subtotal × 1.10 overhead = $61.19/hr break-even", "÷ (1 − 15 % margin) = $71.99/hr dry charge-out"],
      },
      {
        h: "Where rates go wrong",
        body: ["The number that sinks most rates is annual hours. A machine that could do 2,000 hours but actually bills 1,100 costs nearly double per hour what the optimistic rate assumed. Use last year's actual hours, not the brochure. And revisit the rate when fuel moves — at 14 L/hr, a 30-cent fuel change is over $4/hr straight off your margin.", "This is an indicative business calculation, not an NZ market rate — every machine, finance deal and region differs."],
      },
    ],
    related: ["/guides/how-to-price-earthworks", "/tools/labour-charge-out-rate-calculator-nz", "/tools/markup-margin-calculator", "/fleet-management-nz"],
  },
  {
    slug: "how-to-prepare-a-construction-variation",
    name: "How to Prepare a Construction Variation",
    title: "How to Prepare a Construction Variation | Pricing & Records | Fieldlink",
    description: "How to prepare and price a construction variation — instruction, scope, cost build-up by category, markup, records and approval, with a worked pricing example.",
    calculator: { to: "/templates/construction-variation-template", label: "Free Variation Template" },
    product: { to: "/job-management-nz", label: "See Fieldlink Variations", text: "Fieldlink captures variations from site, prices them from your rates and tracks approval against the job — no lost paperwork." },
    quickAnswer: "A defensible variation has four parts: the instruction (who directed the change and when), the scope (exactly what's extra or different), the cost build-up (labour, plant, materials, subcontractors and other, each with markup), and the approval trail. Price it and submit it before the work disappears into the job.",
    sections: [
      {
        h: "1. Anchor it to an instruction",
        body: ["Every variation starts with why. Record the source — engineer's instruction, client email, site meeting minute, latent condition — with a reference and date. 'The client told us on site' is worth very little six months later; an EI number or email is worth everything. If the contract requires notice within a set time, that clock is already running."],
      },
      {
        h: "2. Describe the change, not the argument",
        body: ["Write the scope factually: what was in the contract, what changed, and what the extra or different work is. Keep it to what a reviewer can verify — 'additional 24 m of 300 mm SW line including excavation in rock and reinstatement' beats a paragraph of justification. Attach the marked-up drawing or photos."],
      },
      {
        h: "3. Build the cost by category",
        formula: ["Direct cost = labour + plant + materials + subcontractors + other", "Markup applied per category (own work vs subcontract often differ)", "Variation value = direct cost + markup (+ GST where applicable)"],
        example: ["Labour: 2 crew × 2 days = $2,720 @ 15 % = $3,128", "Plant: digger + truck 2 days = $3,400 @ 15 % = $3,910", "Materials: pipe & bedding = $1,850 @ 15 % = $2,127.50", "Subcontract: reinstatement = $1,200 @ 10 % = $1,320", "Variation total (excl. GST) = $10,485.50"],
      },
      {
        h: "4. Track it to approval",
        body: ["A priced variation that never gets approved is a donation. Track each one through pending → submitted → approved, and don't let approved variations get missed off the next progress claim — they're part of the revised contract value.", "Check your contract's variation and notice clauses; some contracts price directed variations from schedule rates and others from actual cost, and the difference matters when you build the claim."],
        list: ["Number variations sequentially (VO-001, VO-002…) and reference them in claims", "Keep daily records for time-and-materials variations — dockets win arguments", "Update the revised contract value as approvals land"],
      },
    ],
    related: ["/templates/progress-claim-template", "/tools/markup-margin-calculator", "/guides/how-to-price-earthworks", "/job-management-nz"],
  },
{
  "slug": "how-to-calculate-pipe-bedding",
  "name": "How to Calculate Pipe Bedding and Surround",
  "title": "Pipe Bedding & Surround Quantities | NZ Drainage Guide",
  "description": "Calculate pipe bedding and surround volumes, subtract pipe displacement and convert to tonnes. A worked example for drainlayers and civil contractors.",
  "calculator": {
    "to": "/tools/pipe-bedding-calculator",
    "label": "Pipe Bedding Calculator"
  },
  "product": {
    "to": "/drainage-estimating-software",
    "label": "Explore drainage estimating",
    "text": "Keep measured pipe runs, material quantities and your own laying rates together in a Fieldlink drainage estimate."
  },
  "quickAnswer": "For a rectangular trench, bedding volume is length × trench width × bedding depth. Surround volume is the envelope above the bedding minus the volume displaced by the pipe. Keep dimensions in metres, use the pipe outside diameter, and take bedding and cover dimensions from the project detail.",
  "sections": [
    {
      "h": "Start with the specified trench detail",
      "body": [
        "Read the drawing before taking quantities. Record trench width, bedding thickness beneath the pipe, outside pipe diameter and the surround height above the crown. Separate runs with different pipe sizes or trench details. These calculations estimate material quantities; they do not choose an installation detail."
      ],
      "list": [
        "Use outside diameter for displacement, not the nominal pipe label.",
        "Keep bedding below the pipe separate from surround alongside and above it.",
        "Measure changes in width or depth as separate sections.",
        "Treat manholes, fittings and local widening separately rather than hiding them in an allowance."
      ]
    },
    {
      "h": "Calculate bedding and surround in cubic metres",
      "formula": [
        "Bedding = length × width × bedding depth",
        "Pipe displacement = π × outside diameter² ÷ 4 × length",
        "Surround above bedding = length × width × (outside diameter + cover above crown) − pipe displacement",
        "Total bedding and surround = bedding + surround above bedding"
      ],
      "body": [
        "This rectangular model assumes the pipe sits at the top of the bedding layer. If the design uses a shaped bed, multiple pipes, concrete encasement or a different envelope, use that geometry instead."
      ]
    },
    {
      "h": "Worked example: a 40 m pipe run",
      "example": [
        "Illustrative quantities only: length 40 m, width 0.6 m, bedding 0.1 m, pipe outside diameter 0.2 m, cover above crown 0.15 m.",
        "Bedding = 40 × 0.6 × 0.1 = 2.40 m³.",
        "Pipe displacement = π × 0.2² ÷ 4 × 40 = 1.26 m³.",
        "Surround = 40 × 0.6 × (0.2 + 0.15) − 1.26 = 7.14 m³.",
        "Total = 9.54 m³ before any separately stated ordering allowance."
      ]
    },
    {
      "h": "Turn the quantity into an order",
      "body": [
        "Ask the supplier which density or conversion basis applies to the specified material. A compacted volume and a loose delivered volume are different measures; do not apply a loose bulk density to a compacted quantity without a justified conversion. Record any wastage allowance separately so it can be checked."
      ],
      "list": [
        "Check the material designation against the specification.",
        "Confirm delivery quantities, truck access and where material can be stockpiled.",
        "Compare delivered dockets with the measured run as work progresses."
      ]
    }
  ],
  "related": [
    "/tools/pipe-bedding-calculator",
    "/tools/trench-volume-calculator",
    "/guides/how-to-calculate-pipe-fall",
    "/guides/how-to-calculate-aggregate-tonnes"
  ]
},
{
  "slug": "how-to-calculate-aggregate-tonnes",
  "name": "How to Calculate Aggregate Tonnes for a Job",
  "title": "Aggregate Tonnes from m³ | NZ Contractor Guide",
  "description": "Work out aggregate volume and tonnes from area, depth and material density, with a worked example and ordering checklist for NZ civil jobs.",
  "calculator": {
    "to": "/tools/aggregate-calculator",
    "label": "Aggregate Calculator"
  },
  "product": {
    "to": "/civil-job-costing-software-nz",
    "label": "Explore job costing",
    "text": "Track the quoted quantity, supplier deliveries and recorded job costs together in Fieldlink."
  },
  "quickAnswer": "Aggregate volume is area × layer depth in metres. Tonnes = volume × the density appropriate to that volume basis. For a finished compacted layer, use a supported compacted-density assumption and confirm the ordering basis with the supplier.",
  "sections": [
    {
      "h": "Measure each layer separately",
      "body": [
        "Split irregular areas into rectangles or triangles, calculate each area, and add them together. Multiply by the specified finished thickness for each material layer. Keep millimetres and metres consistent: a 150 mm layer is 0.15 m."
      ],
      "list": [
        "Separate basecourse, subbase and drainage material.",
        "Use the measured or designed shape rather than a single maximum width.",
        "Allow for level changes or undercut as separate measured work."
      ]
    },
    {
      "h": "Convert volume to tonnes using a stated assumption",
      "formula": [
        "Area of a rectangle = length × width",
        "Layer volume = area × depth in metres",
        "Estimated tonnes = layer volume × density in tonnes per m³",
        "Order allowance = estimated tonnes × allowance percentage ÷ 100"
      ],
      "body": [
        "There is no single density that fits every aggregate or moisture condition. Use project information, supplier advice or measured records for the material. A product name alone is not a reliable conversion factor."
      ]
    },
    {
      "h": "Worked example: an access area",
      "example": [
        "Area 25 m × 8 m = 200 m².",
        "Finished layer thickness 150 mm = 0.15 m.",
        "Volume = 200 × 0.15 = 30 m³.",
        "At an illustrative compacted density of 2.1 t/m³: 30 × 2.1 = 63 tonnes.",
        "A separately chosen 5% ordering allowance adds 3.15 tonnes: total 66.15 tonnes."
      ],
      "body": [
        "The density and allowance are examples, not a recommendation for a particular aggregate. Replace them with inputs appropriate to the job."
      ]
    },
    {
      "h": "Check what the supplier will deliver",
      "list": [
        "Confirm whether the quote is per tonne, loose cubic metre or load.",
        "Check whether moisture, handling or compaction changes the comparison with your takeoff.",
        "Plan access and staged deliveries to avoid handling material twice.",
        "Reconcile dockets with installed quantities and remaining stock before ordering the final loads."
      ]
    }
  ],
  "related": [
    "/tools/aggregate-calculator",
    "/guides/how-to-calculate-pipe-bedding",
    "/guides/how-to-price-earthworks",
    "/tools/truck-load-calculator"
  ]
},
{
  "slug": "how-to-plan-spoil-removal",
  "name": "How to Plan Spoil Removal and Truck Loads",
  "title": "Spoil Removal & Truck Loads | Civil Contractor Guide",
  "description": "Plan spoil cartage using bank and loose volumes, payload, truck capacity and cycle time. Includes a worked example and a practical site checklist.",
  "calculator": {
    "to": "/tools/truck-load-calculator",
    "label": "Truck Load Calculator"
  },
  "product": {
    "to": "/earthworks-estimating-software",
    "label": "Explore earthworks estimating",
    "text": "Build cartage assumptions into your earthworks estimate, alongside excavation, plant and disposal costs."
  },
  "quickAnswer": "Convert the measured excavation from bank volume to loose volume, then check both truck volume and payload. The usable volume per trip is the smaller of bin capacity and payload divided by loose material density. Divide the loose spoil by that usable volume and round up for whole loads.",
  "sections": [
    {
      "h": "Separate what is dug from what is carted",
      "body": [
        "Start with the excavation takeoff, then subtract material that will genuinely stay on the job. Keep topsoil, reusable fill and other material streams separate if their destinations or handling differ. Bulking changes the space occupied after digging; it does not create extra material mass."
      ],
      "formula": [
        "Loose spoil volume = bank volume × (1 + bulking percentage ÷ 100)"
      ]
    },
    {
      "h": "Check payload as well as bin size",
      "formula": [
        "Payload-limited volume = available payload ÷ loose density",
        "Usable volume per load = smaller of bin capacity and payload-limited volume",
        "Loads = loose spoil volume ÷ usable volume per load, rounded up"
      ],
      "body": [
        "Use the payload available for the actual vehicle, route and operating conditions, confirmed with the carrier. A volume estimate does not authorise a loading weight. Wet or dense material can reach the payload limit before the bin looks full."
      ]
    },
    {
      "h": "Worked example: payload limits the load",
      "example": [
        "Measured spoil: 100 bank m³. Illustrative bulking allowance: 20%.",
        "Loose volume = 100 × 1.2 = 120 m³.",
        "Bin capacity 10 m³; assumed available payload 12 tonnes; loose density 1.5 t/m³.",
        "Payload-limited volume = 12 ÷ 1.5 = 8 m³.",
        "Use 8 m³ per trip: 120 ÷ 8 = 15 loads."
      ]
    },
    {
      "h": "Check the truck cycle against the programme",
      "body": [
        "Add loading, travel out, unloading, return travel and expected queue time. Use measured trips where available. Truck count and daily output depend on both the loading operation and the full cycle, so a faster digger alone may not improve cartage."
      ],
      "list": [
        "Confirm the receiving facility accepts the material before starting.",
        "Check access, turnaround space and delivery windows with the carrier.",
        "Plan for interruptions and record assumptions rather than treating every paid hour as productive.",
        "Compare actual load counts and docket weights with the estimate during the job."
      ]
    }
  ],
  "related": [
    "/tools/spoil-calculator",
    "/tools/truck-cycle-calculator",
    "/guides/how-to-calculate-excavation-volume",
    "/guides/how-to-price-earthworks"
  ]
},
{
  "slug": "how-to-estimate-crew-productivity",
  "name": "How to Estimate Crew Productivity and Duration",
  "title": "Crew Productivity & Job Duration | Contractor Guide",
  "description": "Estimate crew days and labour hours from quantities, observed output and productive hours. A worked example for drainlayers and small construction teams.",
  "calculator": {
    "to": "/tools/crew-productivity-calculator",
    "label": "Crew Productivity Calculator"
  },
  "product": {
    "to": "/civil-crew-scheduling-software-nz",
    "label": "Explore crew planning",
    "text": "Use an estimate to plan the work, then compare allocations and recorded hours as the job progresses in Fieldlink."
  },
  "quickAnswer": "Estimate duration from the whole crew’s output per productive hour: productive hours = quantity ÷ crew output rate. Divide by productive hours per day for crew days. Multiply crew days by people and paid hours per day to estimate paid person-hours. Keep the unit consistent throughout.",
  "sections": [
    {
      "h": "Choose a measurable unit of work",
      "body": [
        "A drainlaying crew might measure metres of a defined pipe installation; a concrete crew might measure square metres of a defined slab activity. Define what the rate includes before reusing it. Excavation alone is different from excavation, bedding, laying, testing and backfill."
      ],
      "list": [
        "Record quantity and unit: metres, m², m³ or items.",
        "Use output for the whole crew, not a per-person rate, in this calculation.",
        "Use comparable completed work to estimate the output rate.",
        "Split changing ground, access or installation details into separate activities."
      ]
    },
    {
      "h": "Distinguish productive time from paid time",
      "body": [
        "Paid hours include time that may not produce measured output: setup, travel, briefings, waiting and pack-down. Use a realistic productive-hours allowance for the activity and keep paid person-hours visible. Do not add the same downtime twice if your historic daily rate already includes it."
      ],
      "formula": [
        "Productive crew-hours = quantity ÷ output per productive crew-hour",
        "Crew days = productive crew-hours ÷ productive hours per day",
        "Paid person-hours = crew days × people in crew × paid hours per day"
      ]
    },
    {
      "h": "Worked example: a defined drainage activity",
      "example": [
        "Quantity: 120 m. Assumed output: 4 m per productive crew-hour.",
        "Crew size: 3 people. Paid day: 8 hours. Productive time: 6 hours/day.",
        "Productive crew-hours = 120 ÷ 4 = 30 hours.",
        "Crew days = 30 ÷ 6 = 5 days.",
        "Paid person-hours = 5 × 3 × 8 = 120 hours."
      ],
      "body": [
        "These are illustrative inputs, not an industry production benchmark. A different trench detail, ground condition or crew setup can change the rate substantially."
      ]
    },
    {
      "h": "Check sensitivity before promising a finish date",
      "body": [
        "At 3 m per productive crew-hour, the same quantity takes 40 productive crew-hours and about 6.67 crew days. At 5 m per hour it takes 24 productive crew-hours and 4 days. Comparing scenarios makes the assumptions visible."
      ],
      "list": [
        "Check material availability and plant support.",
        "Identify inspections, access windows and other dependencies separately.",
        "Convert crew days into an actual work calendar before setting dates.",
        "Compare completed quantity and recorded hours daily; update the remaining-work estimate when conditions change."
      ]
    }
  ],
  "related": [
    "/tools/crew-productivity-calculator",
    "/tools/labour-charge-out-rate-calculator-nz",
    "/guides/how-to-price-earthworks",
    "/templates/site-diary-template"
  ]
}
];

module.exports = { GUIDES };
